import { TProcedure } from "../../../../utils/types";
import {
  simulateTabAbdomenLipoImages,
  simulateTabBblImages,
  simulateTabChinLipoImages,
  simulateTabFaceLiftImages,
  simulateTabHairTransplantImages,
  simulateTabLipFillerImages,
  simulateTabRhinoplastyImages,
  simulateTabTummyTuckImages,
} from "./simulateTabConstants";

export type PhotoGuidanceExample = {
  alt: string;
  imageUrl: string;
  kind: "good" | "too-close" | "covered" | "low-light";
  label: string;
  zoom?: number;
};

export type PhotoUploadGuidance = {
  examples: PhotoGuidanceExample[];
  title: string;
};

type ProcedureGuidanceConfig = {
  coveredImageUrl?: string;
  tooCloseZoom?: number;
  goodImageUrl: string;
  goodPhotoAlt: string;
  title: string;
  requiresExposedArea: boolean;
};

const BAD_AREA_COVERED_IMAGE_URL =
  "https://clinic-os.com/photo-guidance/bad-area-covered.webp";
const CHEST_COVERED_IMAGE_URL =
  "https://clinic-os.com/photo-guidance/chest-covered.webp";
const CHEST_GOOD_IMAGE_URL =
  "https://clinic-os.com/photo-guidance/chest-good.webp";
// The chest reference photo is full-body, so it needs a tighter crop than the
// default to read as "too close".
const CHEST_TOO_CLOSE_ZOOM = 1.95;

const chestGuidance: ProcedureGuidanceConfig = {
  coveredImageUrl: CHEST_COVERED_IMAGE_URL,
  tooCloseZoom: CHEST_TOO_CLOSE_ZOOM,
  goodImageUrl: CHEST_GOOD_IMAGE_URL,
  goodPhotoAlt: "Well-framed front photo showing the full chest",
  title: "Show your full chest, straight on",
  requiresExposedArea: true,
};

const chestAndAbdomenGuidance: ProcedureGuidanceConfig = {
  coveredImageUrl: CHEST_COVERED_IMAGE_URL,
  tooCloseZoom: CHEST_TOO_CLOSE_ZOOM,
  goodImageUrl: CHEST_GOOD_IMAGE_URL,
  goodPhotoAlt: "Well-framed front photo showing the chest and abdomen",
  title: "Show your full chest and abdomen",
  requiresExposedArea: true,
};

const procedureGuidance: Partial<Record<TProcedure, ProcedureGuidanceConfig>> =
  {
    BREAST_AUGMENTATION: chestGuidance,
    BREAST_LIFT: chestGuidance,
    BREAST_REDUCTION: chestGuidance,
    BREAST_LIFT_AND_AUGMENTATION: chestGuidance,
    BBL: {
      goodImageUrl: simulateTabBblImages[0],
      goodPhotoAlt: "Well-framed rear photo showing the full hips",
      title: "Show your full hips from behind",
      requiresExposedArea: true,
    },
    TUMMY_TUCK: {
      goodImageUrl: simulateTabTummyTuckImages[0],
      goodPhotoAlt: "Well-framed front photo showing the full abdomen",
      title: "Show from chest to upper thighs",
      requiresExposedArea: true,
    },
    ABDOMEN_LIPOSUCTION: {
      goodImageUrl: simulateTabAbdomenLipoImages[0],
      goodPhotoAlt: "Well-framed front photo showing the full abdomen",
      title: "Show from chest to upper thighs",
      requiresExposedArea: true,
    },
    FACE_LIFT: {
      goodImageUrl: simulateTabFaceLiftImages[0],
      goodPhotoAlt: "Well-lit photo showing the full face and neck",
      title: "Show your full face and neck",
      requiresExposedArea: false,
    },
    LIP_FILLER: {
      goodImageUrl: simulateTabLipFillerImages[0],
      goodPhotoAlt: "Well-lit photo showing the full centered face",
      title: "Center your full face",
      requiresExposedArea: false,
    },
    RHINOPLASTY: {
      goodImageUrl: simulateTabRhinoplastyImages[0],
      goodPhotoAlt: "Well-lit photo showing the full face and profile",
      title: "Show your full face at the requested angle",
      requiresExposedArea: false,
    },
    CHIN_LIPOSUCTION: {
      goodImageUrl: simulateTabChinLipoImages[0],
      goodPhotoAlt: "Well-lit photo showing the full head and neck",
      title: "Show your full head and neck",
      requiresExposedArea: false,
    },
    HAIR_TRANSPLANT: {
      goodImageUrl: simulateTabHairTransplantImages[0],
      goodPhotoAlt: "Well-lit photo showing the hairline and top of the head",
      title: "Show your hairline and top of head",
      requiresExposedArea: false,
    },
    TUMMY_TUCK_AND_BREAST_LIFT: chestAndAbdomenGuidance,
    TUMMY_TUCK_AND_BREAST_REDUCTION: chestAndAbdomenGuidance,
    TUMMY_TUCK_AND_BREAST_AUGMENTATION: chestAndAbdomenGuidance,
  };

const fallbackGuidance: ProcedureGuidanceConfig = {
  goodImageUrl: CHEST_GOOD_IMAGE_URL,
  goodPhotoAlt: "Well-framed photo with the full treatment area visible",
  title: "Keep the full treatment area visible",
  requiresExposedArea: false,
};

export const getPhotoUploadGuidance = (
  procedure: TProcedure,
): PhotoUploadGuidance => {
  const guidance = procedureGuidance[procedure] ?? fallbackGuidance;
  const thirdExample: PhotoGuidanceExample = guidance.requiresExposedArea
    ? {
        alt: "Bad example because clothing covers the treatment area",
        imageUrl: guidance.coveredImageUrl ?? BAD_AREA_COVERED_IMAGE_URL,
        kind: "covered",
        label: "Covered",
      }
    : {
        alt: "Bad example because the photo is too dark",
        imageUrl: guidance.goodImageUrl,
        kind: "low-light",
        label: "Too dark",
      };

  return {
    title: guidance.title,
    examples: [
      {
        alt: guidance.goodPhotoAlt,
        imageUrl: guidance.goodImageUrl,
        kind: "good",
        label: "Good",
      },
      {
        alt: "Bad example because the treatment area is cropped too closely",
        imageUrl: guidance.goodImageUrl,
        kind: "too-close",
        label: "Too close",
        zoom: guidance.tooCloseZoom,
      },
      thirdExample,
    ],
  };
};
